import * as React from "react";
import { Button, Input, Dropdown, Table, Modal, Header, Segment, Icon, Checkbox, Label, TextArea, Message } from "semantic-ui-react";
import AceEditor from "react-ace";
import {
    fetchAiParseResults,
    continueAiChat,
    AiParseResult,
    extractDatabaseEntryIds,
    fetchTransportOrderById,
    fetchAiRequestsByIds,
    runSingleAiRequest,
    prettifyAiOutput
} from "./aiRequestsApi";

import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/mode-json';
import { TransportOrderJsonViewer } from "../../configChange/AiScripter/TransportOrderJsonViewer";
import { AiRequestRerunModal } from "./aiRequestsRerunModal";
import { AiRequestEditPromptModal } from "./aiRequestEditPromptModal";

const MODEL_OPTIONS = [
    'GPT35',
    'GPT4',
    'GPT4_TURBO',
    'GPT4_O',
    'GPT4_O_Mini',
    'GPT5',
    'GPT5_MINI',
    'Claude37_Sonnet',
    'Claude4_Sonnet',
    'Claude45_Sonnet',
    'Claude45_Haiku',
    'CLAUDE46_SONNET',
    'CLAUDE46_OPUS',
    'DeepSeek_V3',
    'DEEPSEEK_V4_FLASH',
    'GEMINI25_PRO',
    'GEMINI25_FLASH',
    'GPT5_NANO',
    'CLAUDE45_OPUS',
    'CLAUDE45_SONNET_THINKING',
    'CLAUDE45_OPUS_THINKING',
    'CLAUDE46_SONNET_THINKING',
    'CLAUDE46_OPUS_THINKING',
    'GEMINI31_PRO',
    'QWEN36_35B',
    'GEMMA4_26B',
    'CLAUDE48_OPUS',
    'CLAUDE48_OPUS_THINKING',
    'GPT56_SOL',
    'GPT56_TERRA',
    'GPT56_LUNA',
    'QWEN38',
    'QWEN38_THINKING',
];

const LIMIT_OPTIONS = [
    { key: '10', text: '10', value: 10 },
    { key: '25', text: '25', value: 25 },
    { key: '50', text: '50', value: 50 },
    { key: '100', text: '100', value: 100 },
    { key: '250', text: '250', value: 250 },
    { key: '500', text: '500', value: 500 },
    { key: '1000', text: '1000', value: 1000 },
];

// Map between thinking and non-thinking variants for the same family
const THINKING_TO_NORMAL: Record<string, string> = {
    'CLAUDE45_SONNET_THINKING': 'Claude45_Sonnet',
    'CLAUDE45_OPUS_THINKING': 'CLAUDE45_OPUS',
    'CLAUDE46_SONNET_THINKING': 'CLAUDE46_SONNET',
    'CLAUDE46_OPUS_THINKING': 'CLAUDE46_OPUS',
    'CLAUDE48_OPUS_THINKING': 'CLAUDE48_OPUS',
    'QWEN38_THINKING': 'QWEN38',
};
const NORMAL_TO_THINKING: Record<string, string> = {
    'Claude45_Sonnet': 'CLAUDE45_SONNET_THINKING',
    'CLAUDE45_OPUS': 'CLAUDE45_OPUS_THINKING',
    'CLAUDE46_SONNET': 'CLAUDE46_SONNET_THINKING',
    'CLAUDE46_OPUS': 'CLAUDE46_OPUS_THINKING',
    'CLAUDE48_OPUS': 'CLAUDE48_OPUS_THINKING',
    'QWEN38': 'QWEN38_THINKING',
};

interface ConversationMessage {
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
    promptTokens?: number;
    completionTokens?: number;
    price?: number;
    thinkingMessage?: string;
}

interface CompareSlot {
    id: string;
    model: string;
    status: 'idle' | 'loading' | 'done' | 'error';
    output?: string;
    thinkingMessage?: string;
    promptTokens?: number;
    completionTokens?: number;
    price?: number;
    error?: string;
    durationMs?: number;
    showThinking?: boolean;
    prettifiedOutput?: string;
    isPrettified?: boolean;
    prettifyError?: string;
    prettifyLoading?: boolean;
}

const MAX_COMPARE_SLOTS = 4;

type ThinkingViewMode = 'output' | 'thinking' | 'split';

export function AiRequestsViewer() {
    const [results, setResults] = React.useState<AiParseResult[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [searchId, setSearchId] = React.useState("");
    const [searchInput, setSearchInput] = React.useState("");
    const [searchOutput, setSearchOutput] = React.useState("");
    const [typeFilter, setTypeFilter] = React.useState("");
    const [limit, setLimit] = React.useState(50);
    const [autoRefresh, setAutoRefresh] = React.useState(false);
    const [filterThinkingOnly, setFilterThinkingOnly] = React.useState(false);
    const [selectedResult, setSelectedResult] = React.useState<AiParseResult | null>(null);
    const [previewOpen, setPreviewOpen] = React.useState(false);
    const [chatOpen, setChatOpen] = React.useState(false);
    const [conversation, setConversation] = React.useState<ConversationMessage[]>([]);
    const [userInput, setUserInput] = React.useState("");
    const [selectedModel, setSelectedModel] = React.useState("");
    const [originalModel, setOriginalModel] = React.useState("");
    const [conversationLoading, setConversationLoading] = React.useState(false);
    const [showModelWarning, setShowModelWarning] = React.useState(false);
    const [jsonFormatError, setJsonFormatError] = React.useState("");
    const [prettifiedOutput, setPrettifiedOutput] = React.useState("");
    const chatEndRef = React.useRef<HTMLDivElement>(null);
    const [isPrettified, setIsPrettified] = React.useState(false);
    const [prettifyLoading, setPrettifyLoading] = React.useState(false);
    const [transportOrderMode, setTransportOrderMode] = React.useState(false);
    const [transportOrderId, setTransportOrderId] = React.useState("");
    const [loadedTransportOrder, setLoadedTransportOrder] = React.useState<any>(null);
    const [splitView, setSplitView] = React.useState(false);
    const [transportOrderJson, setTransportOrderJson] = React.useState("");
    const [splitPosition, setSplitPosition] = React.useState(30);
    const [isResizing, setIsResizing] = React.useState(false);
    const [inputMode, setInputMode] = React.useState<'id' | 'json'>('id');
    const [transportOrderJsonInput, setTransportOrderJsonInput] = React.useState("");
    const [jsonInputModalOpen, setJsonInputModalOpen] = React.useState(false);
    const [base64Warning, setBase64Warning] = React.useState<{ removed: number; totalSize: number } | null>(null);

    const [compareOpen, setCompareOpen] = React.useState(false);
    const [compareSource, setCompareSource] = React.useState<AiParseResult | null>(null);
    const [compareSlots, setCompareSlots] = React.useState<CompareSlot[]>([]);
    const [compareRunning, setCompareRunning] = React.useState(false);
    const [compareShowSourceThinking, setCompareShowSourceThinking] = React.useState(false);
    const [sourcePrettifiedOutput, setSourcePrettifiedOutput] = React.useState<string>("");
    const [sourceIsPrettified, setSourceIsPrettified] = React.useState(false);
    // Thinking-related view state

    const [sourceShowInput, setSourceShowInput] = React.useState(false);
    const [sourcePrettifyError, setSourcePrettifyError] = React.useState("");
    const [sourcePrettifyLoading, setSourcePrettifyLoading] = React.useState(false);

    const [thinkingViewMode, setThinkingViewMode] = React.useState<ThinkingViewMode>('output');
    const [expandedThinkingMap, setExpandedThinkingMap] = React.useState<Record<number, boolean>>({});

    const [rerunOpen, setRerunOpen] = React.useState(false);
    const [rerunSource, setRerunSource] = React.useState<AiParseResult | null>(null);

    const [editPromptOpen, setEditPromptOpen] = React.useState(false);
    const [editPromptSource, setEditPromptSource] = React.useState<AiParseResult | null>(null);

    function openRerun(result: AiParseResult) {
        setRerunSource(result);
        setPreviewOpen(false);
        setRerunOpen(true);
    }

    function openEditPrompt(result: AiParseResult) {
        setEditPromptSource(result);
        setPreviewOpen(false);
        setEditPromptOpen(true);
    }

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsResizing(true);
        e.preventDefault();
    };

    // Models that always run in thinking mode and have no non-thinking counterpart
    const ALWAYS_THINKING_MODELS = new Set<string>([
        'DEEPSEEK_V4_FLASH',
        'GPT56_SOL',
        'GPT56_TERRA',
        'GPT56_LUNA',
    ]);

    function isThinkingModel(model: string): boolean {
        return model in THINKING_TO_NORMAL || ALWAYS_THINKING_MODELS.has(model);
    }

    function canSwitchThinkingVariant(model: string): boolean {
        return model in THINKING_TO_NORMAL || model in NORMAL_TO_THINKING;
    }

    React.useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing) return;
            const container = document.getElementById('split-container');
            if (!container) return;
            const containerRect = container.getBoundingClientRect();
            const newPosition = ((e.clientX - containerRect.left) / containerRect.width) * 100;
            if (newPosition >= 15 && newPosition <= 45) {
                setSplitPosition(newPosition);
            }
        };
        const handleMouseUp = () => setIsResizing(false);
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing]);

    React.useEffect(() => {
        doLoadResults();
    }, []);

    React.useEffect(() => {
        if (autoRefresh && !loading && !transportOrderMode) {
            const interval = setInterval(() => {
                doLoadResults();
            }, 15000);
            return () => clearInterval(interval);
        }
    }, [autoRefresh, loading, transportOrderMode]);

    React.useEffect(() => {
        if (chatOpen && chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [conversation]);

    // Derived list with client-side thinking filter
    const displayedResults = React.useMemo(() => {
        return filterThinkingOnly ? results.filter(r => !!r.thinkingMessage) : results;
    }, [results, filterThinkingOnly]);

    async function doLoadResults(overrideParams?: {
        searchId?: string;
        typeFilter?: string;
        searchInput?: string;
        searchOutput?: string;
        limit?: number;
    }) {
        setLoading(true);
        try {
            const params = overrideParams || {};
            const data = await fetchAiParseResults({
                _id: (params.searchId !== undefined ? params.searchId : searchId) || undefined,
                type: (params.typeFilter !== undefined ? params.typeFilter : typeFilter) || undefined,
                inputContains: (params.searchInput !== undefined ? params.searchInput : searchInput) || undefined,
                outputContains: (params.searchOutput !== undefined ? params.searchOutput : searchOutput) || undefined,
                limit: params.limit !== undefined ? params.limit : limit,
                hasThinking: filterThinkingOnly ? true : undefined
            });
            setResults(data);
        } catch (e) {
            console.error("Failed to load AI requests:", e);
        } finally {
            setLoading(false);
        }
    }

    function removeBase64Attachments(obj: any): { cleaned: any; stats: { removed: number; totalSize: number } } {
        let removedCount = 0;
        let totalSize = 0;

        function processValue(value: any): any {
            if (Array.isArray(value)) {
                return value.map(item => processValue(item));
            } else if (value !== null && typeof value === 'object') {
                const newObj: any = {};
                for (const key in value) {
                    if ((key === 'uploadOnlyBase64AttachmentContent' ||
                            key === 'attachment' ||
                            key === 'base64' ||
                            key === 'content') &&
                        typeof value[key] === 'string' &&
                        value[key].length > 1000) {
                        const originalSize = value[key].length;
                        totalSize += originalSize;
                        removedCount++;
                        const preview = value[key].substring(0, 20);
                        newObj[key] = `[BASE64_REMOVED - Preview: ${preview}...]`;
                    } else if (key === 'attachments' && Array.isArray(value[key])) {
                        newObj[key] = value[key].map((att: any) => {
                            if (att && typeof att === 'object') return processValue(att);
                            return att;
                        });
                    } else {
                        newObj[key] = processValue(value[key]);
                    }
                }
                return newObj;
            }
            return value;
        }

        const cleaned = processValue(obj);
        return { cleaned, stats: { removed: removedCount, totalSize } };
    }

    async function loadTransportOrderFromJson() {
        if (!transportOrderJsonInput.trim()) return;

        setLoading(true);
        setBase64Warning(null);

        try {
            let cleanedJsonString = transportOrderJsonInput;
            const base64Pattern = /"(uploadOnlyBase64AttachmentContent|attachment|base64|content)"\s*:\s*"([^"]{1000,})"/g;
            let match;
            let totalRemoved = 0;
            let totalSize = 0;

            while ((match = base64Pattern.exec(transportOrderJsonInput)) !== null) {
                const fieldName = match[1];
                const base64Content = match[2];
                totalSize += base64Content.length;
                totalRemoved++;
                const preview = base64Content.substring(0, 20);
                const replacement = `"${fieldName}":"[BASE64_REMOVED - Preview: ${preview}...]"`;
                cleanedJsonString = cleanedJsonString.replace(match[0], replacement);
            }

            if (totalRemoved > 0) {
                setBase64Warning({ removed: totalRemoved, totalSize });
            }

            const parsedOrder = JSON.parse(cleanedJsonString);
            const order = Array.isArray(parsedOrder)
                ? parsedOrder[0]?.transportOrders?.[0] || parsedOrder[0]
                : parsedOrder.transportOrders?.[0] || parsedOrder;

            setLoadedTransportOrder(order);

            const { cleaned: furtherCleaned } = removeBase64Attachments(order);
            setTransportOrderJson(JSON.stringify(furtherCleaned, null, 2));

            const ids = extractDatabaseEntryIds(order);
            if (ids.length === 0) {
                alert('No database entry IDs found in this transport order');
                setResults([]);
            } else {
                const aiResults = await fetchAiRequestsByIds(ids);
                setResults(aiResults);
            }

            setTransportOrderMode(true);
            setJsonInputModalOpen(false);
            setSplitView(true);
            setTransportOrderJsonInput("");
        } catch (e: any) {
            console.error("Failed to parse transport order JSON:", e);
            alert(`Invalid JSON: ${e.message}`);
        } finally {
            setLoading(false);
        }
    }

    async function loadTransportOrder() {
        if (!transportOrderId.trim()) return;
        setLoading(true);
        setBase64Warning(null);

        try {
            const order = await fetchTransportOrderById(transportOrderId);
            setLoadedTransportOrder(order);
            const { cleaned, stats } = removeBase64Attachments(order);
            if (stats.removed > 0) setBase64Warning(stats);
            setTransportOrderJson(JSON.stringify(cleaned, null, 2));

            const ids = extractDatabaseEntryIds(order);
            if (ids.length === 0) {
                alert('No database entry IDs found in this transport order');
                setResults([]);
            } else {
                const aiResults = await fetchAiRequestsByIds(ids);
                setResults(aiResults);
            }

            setTransportOrderMode(true);
            setSplitView(true);
        } catch (e: any) {
            console.error("Failed to load transport order:", e);
            alert(`Error: ${e.message}`);
        } finally {
            setLoading(false);
        }
    }

    async function exitTransportOrderMode() {
        setTransportOrderMode(false);
        setTransportOrderId("");
        setTransportOrderJsonInput("");
        setLoadedTransportOrder(null);
        setSplitView(false);
        setTransportOrderJson("");
        setChatOpen(false);
        setPreviewOpen(false);
        setBase64Warning(null);
        await doLoadResults();
    }

    function handleSearch() {
        if (transportOrderMode) return;
        doLoadResults();
    }

    async function clearFilters() {
        setSearchId("");
        setSearchInput("");
        setSearchOutput("");
        setTypeFilter("");
        setLimit(50);
        setFilterThinkingOnly(false);
        await doLoadResults({
            searchId: "",
            typeFilter: "",
            searchInput: "",
            searchOutput: "",
            limit: 50
        });
    }

    function mapModelToEnum(modelString: string): string {
        const mapping: Record<string, string> = {
            'gpt-3.5-turbo': 'GPT35',
            'gpt-4': 'GPT4',
            'gpt-4o': 'GPT4_O',
            'gpt-5': 'GPT5',
            'gpt-5-mini': 'GPT5_MINI',
            'gpt-4o-mini': 'GPT4_O_Mini',
            'gpt-4-turbo': 'GPT4_TURBO',
            'deepseek-chat': 'DeepSeek_V3',
            'deepseek-v4-flash': 'DEEPSEEK_V4_FLASH',
            'claude-3-5-sonnet-20241022': 'Claude35_Sonnet',
            'claude-sonnet-4-5-20250929': 'Claude45_Sonnet',
            'claude-haiku-4-5-20251001': 'Claude45_Haiku',
            'claude-sonnet-4-20250514': 'Claude4_Sonnet',
            'gemini-2.5-pro': 'GEMINI25_PRO',
            'gemini-2.5-flash': 'GEMINI25_FLASH',
            'claude-3-7-sonnet-20250219': 'Claude37_Sonnet',
            'gpt-5-nano': 'GPT5_NANO',
            'claude-opus-4-5-20251101': 'CLAUDE45_OPUS',
            'claude-sonnet-4-6': 'CLAUDE46_SONNET',
            'claude-opus-4-6': 'CLAUDE46_OPUS',
            'claude-opus-4-8': 'CLAUDE48_OPUS',
            // Thinking variants
            'claude-sonnet-4-5-20250929_thinking': 'CLAUDE45_SONNET_THINKING',
            'unsloth/Qwen3.8-27B-GGUF:UD-Q8_K_XL': 'QWEN38',
            'claude-sonnet-4-6_thinking': 'CLAUDE46_SONNET_THINKING',
            'claude-opus-4-6_thinking': 'CLAUDE46_OPUS_THINKING',
            'claude-sonnet-4-8_thinking': 'CLAUDE48_OPUS_THINKING',

            'gemini-3.1-pro-preview': 'GEMINI31_PRO',

            'qwen/qwen3.6-35b-a3b': 'QWEN36_35B',
            'google/gemma-4-26b-a4b': 'GEMMA4_26B',
            'gpt-5.6-sol': 'GPT56_SOL',
            'gpt-5.6-terra': 'GPT56_TERRA',
            'gpt-5.6-luna': 'GPT56_LUNA',

        };
        return mapping[modelString] || modelString;
    }

    // Resolves the correct enum for a stored result. Qwen3.8 uses one model string
    // for both variants, so we upgrade to the thinking enum when the original run
    // actually produced a thinking block.
    function mapResultToEnum(result: AiParseResult): string {
        const base = mapModelToEnum(result.model);
        if (base === 'QWEN38' && result.thinkingMessage) {
            return 'QWEN38_THINKING';
        }
        return base;
    }

    async function handlePrettifyOutput() {
        if (!selectedResult) return;
        setPrettifyLoading(true);
        setJsonFormatError("");
        try {
            const res = await prettifyAiOutput(selectedResult.rawOutput || selectedResult.output);
            if (!res.isJson) {
                setPrettifiedOutput("");
                setIsPrettified(false);
                setJsonFormatError("Not valid JSON - cannot prettify");
            } else {
                setPrettifiedOutput(res.prettified);
                setIsPrettified(true);
            }
        } catch (e: any) {
            setIsPrettified(false);
            setJsonFormatError(e?.message || "Formatting failed");
        } finally {
            setPrettifyLoading(false);
        }
    }

    function openPreview(result: AiParseResult) {
        setSelectedResult(result);
        setPrettifiedOutput("");
        setJsonFormatError("");
        setIsPrettified(false);
        // Reset thinking view based on whether thinking exists
        setThinkingViewMode(result.thinkingMessage ? 'split' : 'output');
        setPreviewOpen(true);
    }

    function handleShowOriginal() {
        setPrettifiedOutput("");
        setJsonFormatError("");
        setIsPrettified(false);
    }

    function openChat(result: AiParseResult) {
        const modelEnum = mapResultToEnum(result);
        setSelectedModel(modelEnum);
        setOriginalModel(modelEnum);
        setShowModelWarning(false);
        setExpandedThinkingMap({});

        const initialConversation: ConversationMessage[] = [
            {
                role: 'user',
                content: result.input,
                timestamp: result.timestamp
            },
            {
                role: 'assistant',
                content: result.rawOutput || result.output,
                timestamp: result.timestamp,
                promptTokens: result.promptTokens,
                completionTokens: result.completionTokens,
                price: result.totalPrice,
                thinkingMessage: result.thinkingMessage
            }
        ];

        setConversation(initialConversation);
        setPreviewOpen(false);
        setChatOpen(true);
        setUserInput("");
    }

    async function sendMessage() {
        if (!userInput.trim()) return;

        const newMessage: ConversationMessage = {
            role: 'user',
            content: userInput,
            timestamp: new Date().toISOString()
        };

        const updatedConversation = [...conversation, newMessage];
        setConversation(updatedConversation);
        setUserInput("");
        setConversationLoading(true);

        try {

            const messages = updatedConversation.map(msg => {
                if (msg.role === 'assistant' && msg.thinkingMessage && msg.thinkingMessage.trim().length > 0) {
                    return {
                        role: msg.role,
                        content:
                            `<thinking_block_generated_for_this_message>\n${msg.thinkingMessage.trim()}\n</thinking_block_generated_for_this_message>\n\n` +
                            `${msg.content}`
                    };
                }
                return {
                    role: msg.role,
                    content: msg.content
                };
            });

            const conversationJson = JSON.stringify(messages);
            const response = await continueAiChat(conversationJson, selectedModel);

            const assistantMessage: ConversationMessage = {
                role: 'assistant',
                content: response.output,
                timestamp: new Date().toISOString(),
                promptTokens: response.usageTokensNumber,
                completionTokens: response.completionTokensNumber,
                price: response.totalPromptPrice,
                thinkingMessage: response.thinkingMessage
            };

            setConversation([...updatedConversation, assistantMessage]);
        } catch (e: any) {
            console.error("Failed to send message:", e);
            alert(`Error: ${e.message}`);
        } finally {
            setConversationLoading(false);
        }
    }

    function copyToClipboard(text: string) {
        navigator.clipboard.writeText(text);
    }

    function handleModelChange(newModel: string) {
        if (newModel !== originalModel && !showModelWarning) {
            setShowModelWarning(true);
        }
        setSelectedModel(newModel);
    }

    function toggleThinkingMode() {
        if (isThinkingModel(selectedModel)) {
            const normal = THINKING_TO_NORMAL[selectedModel];
            if (normal) handleModelChange(normal);
        } else {
            const thinking = NORMAL_TO_THINKING[selectedModel];
            if (thinking) handleModelChange(thinking);
        }
    }

    function removeLastPair() {
        if (conversation.length > 2) {
            setConversation(conversation.slice(0, -2));
        }
    }

    function calculateTotals() {
        let totalInput = 0;
        let totalOutput = 0;
        let totalPrice = 0;
        conversation.forEach((msg) => {
            if (msg.role === 'assistant') {
                if (msg.promptTokens) totalInput += msg.promptTokens;
                if (msg.completionTokens) totalOutput += msg.completionTokens;
                if (msg.price) totalPrice += msg.price;
            }
        });
        return { totalInput, totalOutput, totalPrice };
    }

    function isModelRecognized(model: string): boolean {
        return MODEL_OPTIONS.includes(model);
    }

    function newCompareSlotId() {
        return Math.random().toString(36).slice(2) + Date.now().toString(36);
    }

    function pickDefaultModel(exclude: Set<string>): string {
        return MODEL_OPTIONS.find(m => !exclude.has(m)) || MODEL_OPTIONS[0];
    }

    function openCompare(result: AiParseResult) {
        setCompareSource(result);
        const sourceEnum = mapResultToEnum(result);
        const defaultOther = pickDefaultModel(new Set([sourceEnum]));
        setCompareSlots([{
            id: newCompareSlotId(),
            model: defaultOther,
            status: 'idle'
        }]);
        setCompareShowSourceThinking(false);
        setSourcePrettifiedOutput("");
        setSourceIsPrettified(false);
        setCompareOpen(true);
    }

    function addCompareSlot() {
        if (compareSlots.length >= MAX_COMPARE_SLOTS) return;
        const sourceEnum = compareSource ? mapResultToEnum(compareSource) : '';
        const used = new Set<string>([sourceEnum, ...compareSlots.map(s => s.model)]);
        setCompareSlots(prev => [...prev, {
            id: newCompareSlotId(),
            model: pickDefaultModel(used),
            status: 'idle'
        }]);
    }

    function removeCompareSlot(id: string) {
        setCompareSlots(prev => prev.filter(s => s.id !== id));
    }

    function updateCompareSlot(id: string, patch: Partial<CompareSlot>) {
        setCompareSlots(prev => prev.map(s => (s.id === id ? { ...s, ...patch } : s)));
    }

    /** Changing the model resets THAT slot only. Other completed slots stay untouched. */
    function changeSlotModel(id: string, newModel: string) {
        setCompareSlots(prev => prev.map(s => s.id === id ? {
            id: s.id,
            model: newModel,
            status: 'idle'
        } : s));
    }

    async function runSingleSlot(slotId: string) {
        if (!compareSource) return;
        const slot = compareSlots.find(s => s.id === slotId);
        if (!slot) return;

        updateCompareSlot(slotId, {
            status: 'loading',
            error: undefined,
            output: undefined,
            thinkingMessage: undefined,
            promptTokens: undefined,
            completionTokens: undefined,
            price: undefined,
            durationMs: undefined,
            prettifiedOutput: undefined,
            isPrettified: false,
            prettifyError: undefined,
            prettifyLoading: false,
        });

        const start = Date.now();
        try {
            const res = await runSingleAiRequest(compareSource.input, slot.model);
            updateCompareSlot(slotId, {
                status: 'done',
                output: res.output,
                thinkingMessage: res.thinkingMessage,
                promptTokens: res.promptTokens,
                completionTokens: res.completionTokens,
                price: res.price,
                durationMs: Date.now() - start
            });
        } catch (e: any) {
            updateCompareSlot(slotId, {
                status: 'error',
                error: e?.message || 'Unknown error',
                durationMs: Date.now() - start
            });
        }
    }

    /** Only runs slots that are pending (idle/error). Completed slots are NOT re-run. */
    async function runComparison() {
        if (!compareSource || compareRunning) return;
        const toRun = compareSlots.filter(s => s.status === 'idle' || s.status === 'error');
        if (toRun.length === 0) return;
        setCompareRunning(true);
        try {
            await Promise.all(toRun.map(s => runSingleSlot(s.id)));
        } finally {
            setCompareRunning(false);
        }
    }

    function clearAllCompareResults() {
        setCompareSlots(prev => prev.map(s => ({
            id: s.id, model: s.model, status: 'idle'
        })));
    }

    function closeCompare() {
        if (compareRunning) {
            if (!window.confirm('Comparison is still running. Close anyway?')) return;
        }
        setCompareOpen(false);
    }

    async function prettifySlot(slotId: string) {
        const slot = compareSlots.find(s => s.id === slotId);
        if (!slot || !slot.output) return;
        updateCompareSlot(slotId, { prettifyLoading: true, prettifyError: undefined });
        try {
            const res = await prettifyAiOutput(slot.output);
            if (!res.isJson) {
                updateCompareSlot(slotId, { prettifyLoading: false, prettifyError: 'Not valid JSON' });
                setTimeout(() => updateCompareSlot(slotId, { prettifyError: undefined }), 4000);
                return;
            }
            updateCompareSlot(slotId, {
                prettifiedOutput: res.prettified,
                isPrettified: true,
                prettifyLoading: false,
                prettifyError: undefined
            });
        } catch (e: any) {
            updateCompareSlot(slotId, { prettifyLoading: false, prettifyError: e?.message || 'Formatting failed' });
            setTimeout(() => updateCompareSlot(slotId, { prettifyError: undefined }), 4000);
        }
    }

    function unprettifySlot(slotId: string) {
        updateCompareSlot(slotId, {
            prettifiedOutput: undefined,
            isPrettified: false,
            prettifyError: undefined
        });
    }

    async function prettifySource() {
        if (!compareSource) return;
        setSourcePrettifyLoading(true);
        setSourcePrettifyError("");
        try {
            const res = await prettifyAiOutput(compareSource.rawOutput || compareSource.output);
            if (!res.isJson) {
                setSourcePrettifyError("Not valid JSON");
                setTimeout(() => setSourcePrettifyError(""), 4000);
                return;
            }
            setSourcePrettifiedOutput(res.prettified);
            setSourceIsPrettified(true);
        } catch (e: any) {
            setSourcePrettifyError(e?.message || "Formatting failed");
            setTimeout(() => setSourcePrettifyError(""), 4000);
        } finally {
            setSourcePrettifyLoading(false);
        }
    }

    function unprettifySource() {
        setSourcePrettifiedOutput("");
        setSourceIsPrettified(false);
        setSourcePrettifyError("");
    }

    const pendingSlotsCount = compareSlots.filter(s => s.status === 'idle' || s.status === 'error').length;

    function toggleExpandedThinking(idx: number) {
        setExpandedThinkingMap(prev => ({ ...prev, [idx]: !prev[idx] }));
    }

    /* ===========================
       Render: Output / Thinking panel for preview
       =========================== */
    function renderOutputAndThinkingPanels(compact: boolean) {
        if (!selectedResult) return null;

        const hasThinking = !!selectedResult.thinkingMessage;
        const outputValue = prettifiedOutput || selectedResult.rawOutput || selectedResult.output;
        const fontSize = compact ? 13 : 14;

        const showOutput = thinkingViewMode === 'output' || thinkingViewMode === 'split';
        const showThinking = hasThinking && (thinkingViewMode === 'thinking' || thinkingViewMode === 'split');

        return (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    height: compact ? 'auto' : '28px',
                    marginBottom: compact ? '5px' : '10px',
                    flexWrap: 'wrap',
                    gap: '6px'
                }}>
                    <Header size="small" style={{ margin: 0, lineHeight: '28px' }}>
                        Output {isPrettified && <Label size="tiny" color="green">Prettified</Label>}
                        {hasThinking && <Label size="tiny" color="grey"><Icon name="lightbulb outline" /> Thinking available</Label>}
                    </Header>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        {hasThinking && (
                            <Button.Group size="mini">
                                <Button
                                    active={thinkingViewMode === 'output'}
                                    onClick={() => setThinkingViewMode('output')}
                                >Output</Button>
                                <Button
                                    active={thinkingViewMode === 'thinking'}
                                    onClick={() => setThinkingViewMode('thinking')}
                                >Thinking</Button>
                                <Button
                                    active={thinkingViewMode === 'split'}
                                    onClick={() => setThinkingViewMode('split')}
                                >Split</Button>
                            </Button.Group>
                        )}
                        {jsonFormatError && (
                            <Label color="red" size="tiny">{jsonFormatError}</Label>
                        )}
                        <Button size="mini" onClick={handleShowOriginal} disabled={!isPrettified}>
                            <Icon name="undo" /> Original
                        </Button>
                        <Button size="mini" onClick={handlePrettifyOutput}
                                loading={prettifyLoading} disabled={prettifyLoading}>
                            <Icon name="code" /> Prettify
                        </Button>
                    </div>
                </div>

                <div style={{ flex: 1, display: 'flex', minHeight: 0, gap: '10px' }}>
                    {showOutput && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            {thinkingViewMode === 'split' && (
                                <div style={{ fontSize: '0.75em', color: '#666', marginBottom: '3px' }}>OUTPUT</div>
                            )}
                            <AceEditor
                                key={`output-${selectedResult._id}-${thinkingViewMode}-${isPrettified ? 'p' : 'r'}`}
                                mode="json"
                                theme="monokai"
                                value={outputValue}
                                readOnly={true}
                                width="100%"
                                height="100%"
                                showPrintMargin={false}
                                fontSize={fontSize}
                                wrapEnabled={true}
                            />
                        </div>
                    )}
                    {showThinking && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            {thinkingViewMode === 'split' && (
                                <div style={{ fontSize: '0.75em', color: '#232323', marginBottom: '3px' }}>
                                    💭 THINKING
                                </div>
                            )}
                            <AceEditor
                                key={`thinking-${selectedResult._id}-${thinkingViewMode}`}
                                mode="text"
                                theme="monokai"
                                value={selectedResult.thinkingMessage || ""}
                                readOnly={true}
                                width="100%"
                                height="100%"
                                showPrintMargin={false}
                                fontSize={fontSize}
                                wrapEnabled={true}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    function renderInlinePreview() {
        if (!selectedResult) return null;
        const hasThinking = !!selectedResult.thinkingMessage;

        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
                backgroundColor: '#fff'
            }}>
                {/* Top bar (compact) */}
                <div style={{
                    padding: '8px 14px',
                    backgroundColor: '#2c3e50',
                    color: '#fff',
                    flexShrink: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                <span style={{fontWeight: 'bold'}}>
                    AI Request Preview
                    {hasThinking && (
                        <Label color="grey" size="mini" style={{marginLeft: 8}}>
                            <Icon name="lightbulb outline"/> Thinking
                        </Label>
                    )}
                </span>
                    <Button size="mini" icon basic inverted onClick={() => setPreviewOpen(false)}>
                        <Icon name="close"/>
                    </Button>
                </div>

                <div style={{
                    padding: '6px 14px',
                    backgroundColor: '#f4f6f8',
                    borderBottom: '1px solid #e1e4e8',
                    display: 'flex',
                    gap: '14px',
                    flexWrap: 'wrap',
                    fontSize: '0.85em',
                    flexShrink: 0
                }}>
                    <span><strong>ID:</strong> {selectedResult._id}</span>
                    <span><strong>Type:</strong> {selectedResult.type}</span>
                    <span><strong>Model:</strong> {selectedResult.model}</span>
                    <span><strong>Tokens:</strong> {selectedResult.promptTokens || '-'} / {selectedResult.completionTokens || '-'}</span>
                    <span><strong>Price:</strong> ${selectedResult.totalPrice?.toFixed(4)}</span>
                </div>

                {/* Editors */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    padding: '8px',
                    minHeight: 0,
                    gap: '8px',
                    overflow: 'hidden'
                }}>
                    <div style={{flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0}}>
                        <Header size="small" style={{margin: '0 0 4px 0', flexShrink: 0}}>Input</Header>
                        <div style={{flex: 1, minHeight: 0}}>
                            <AceEditor
                                mode="json"
                                theme="monokai"
                                value={selectedResult.input}
                                readOnly
                                width="100%"
                                height="100%"
                                showPrintMargin={false}
                                fontSize={13}
                                wrapEnabled
                            />
                        </div>
                    </div>
                    {renderOutputAndThinkingPanels(true)}
                </div>

                <div style={{
                    padding: '8px 14px',
                    borderTop: '1px solid #e1e4e8',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    flexShrink: 0
                }}>
                    <Button color="teal" onClick={() => {
                        setPreviewOpen(false);
                        openCompare(selectedResult!);
                    }}>
                        <Icon name="exchange"/> Compare
                    </Button>

                    <Button color="olive" onClick={() => openRerun(selectedResult!)}>
                        <Icon name="redo"/> Rerun
                    </Button>

                    <Button color="orange" onClick={() => openEditPrompt(selectedResult!)}>
                        <Icon name="edit outline"/> Edit Prompt
                    </Button>

                    <Button primary onClick={() => openChat(selectedResult)}>
                        Open Chat
                    </Button>
                </div>
            </div>
        );
    }

    function renderPreviewModal() {
        if (splitView && transportOrderMode) return null;
        const hasThinking = !!selectedResult?.thinkingMessage;

        return (
            <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} size="fullscreen">
                <Header style={{padding: '15px', backgroundColor: '#2c3e50', color: '#fff', margin: 0}}>
                    AI Request Preview
                    {hasThinking && (
                        <Label color="grey" size="small" style={{marginLeft: '10px'}}>
                            <Icon name="lightbulb outline"/> Thinking
                        </Label>
                    )}
                </Header>
                <Modal.Content style={{
                    padding: '20px',
                    height: 'calc(100vh - 200px)',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {selectedResult && (
                        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <Segment>
                                <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap', fontSize: '0.9em' }}>
                                    <div><strong>ID:</strong> {selectedResult._id}</div>
                                    <div><strong>Type:</strong> {selectedResult.type}</div>
                                    <div><strong>Model:</strong> {selectedResult.model}</div>
                                    <div><strong>Tokens:</strong> {selectedResult.promptTokens || '-'} / {selectedResult.completionTokens || '-'}</div>
                                    <div><strong>Price:</strong> ${selectedResult.totalPrice?.toFixed(4)}</div>
                                    <div><strong>Cache Hits:</strong> {selectedResult.counter}x</div>
                                    <div><strong>Time:</strong> {new Date(selectedResult.timestamp).toLocaleString()}</div>
                                </div>
                            </Segment>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '20px',
                                flex: 1,
                                minHeight: 0
                            }}>
                                <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                    <div style={{ height: '28px', marginBottom: '10px' }}>
                                        <Header size="small" style={{ margin: 0, lineHeight: '28px' }}>Input</Header>
                                    </div>
                                    <AceEditor
                                        mode="json"
                                        theme="monokai"
                                        value={selectedResult.input}
                                        readOnly={true}
                                        width="100%"
                                        height="100%"
                                        showPrintMargin={false}
                                        fontSize={14}
                                        wrapEnabled={true}
                                    />
                                </div>
                                {renderOutputAndThinkingPanels(false)}
                            </div>
                        </div>
                    )}
                </Modal.Content>

                <Modal.Actions>
                    <Button onClick={() => setPreviewOpen(false)}>Close</Button>
                    <Button color="teal" onClick={() => { setPreviewOpen(false); openCompare(selectedResult!); }}>
                        <Icon name="exchange" /> Compare
                    </Button>

                    <Button color="olive" onClick={() => openRerun(selectedResult!)}>
                        <Icon name="redo" /> Rerun
                    </Button>

                    <Button color="orange" onClick={() => openEditPrompt(selectedResult!)}>
                        <Icon name="edit outline" /> Edit Prompt
                    </Button>

                    <Button primary onClick={() => openChat(selectedResult!)}>Open Chat</Button>
                </Modal.Actions>
            </Modal>
        );
    }

    function renderJsonInputModal() {
        return (
            <Modal open={jsonInputModalOpen} onClose={() => setJsonInputModalOpen(false)} size="large">
                <Header>
                    <Icon name="paste" />
                    Paste Transport Order JSON
                </Header>
                <Modal.Content>
                    <Message info>
                        <Message.Header>Large File Handling</Message.Header>
                        <p>Base64 attachments will be removed.</p>
                    </Message>
                    <TextArea
                        placeholder="Paste your transport order JSON here..."
                        value={transportOrderJsonInput}
                        onChange={(e) => setTransportOrderJsonInput(e.target.value)}
                        rows={20}
                        style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px' }}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => {
                        setJsonInputModalOpen(false);
                        setTransportOrderJsonInput("");
                    }}>Cancel</Button>
                    <Button
                        primary
                        onClick={loadTransportOrderFromJson}
                        loading={loading}
                        disabled={!transportOrderJsonInput.trim()}
                    >
                        <Icon name="check" /> Load Order
                    </Button>
                </Modal.Actions>
            </Modal>
        );
    }

    function renderChatView(isInSplitPanel: boolean = false) {
        const totals = calculateTotals();
        const modelRecognized = isModelRecognized(selectedModel);
        const thinkingActive = isThinkingModel(selectedModel);
        const canToggleThinking = canSwitchThinkingVariant(selectedModel);

        return (
            <div style={{ height: '100%', overflow: 'auto', backgroundColor: '#f5f5f5' }}>
                <div style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    padding: isInSplitPanel ? '10px' : '15px',
                    backgroundColor: '#fff',
                    borderBottom: '2px solid #ddd'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                        flexWrap: 'wrap',
                        gap: '10px'
                    }}>
                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                            <Button size={isInSplitPanel ? 'small' : undefined} onClick={() => setChatOpen(false)}>
                                <Icon name="arrow left" /> Back
                            </Button>
                            {isInSplitPanel && (
                                <Button size="small" icon onClick={() => setSplitView(false)} title="Expand to full screen">
                                    <Icon name="expand" />
                                </Button>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: isInSplitPanel ? '0.85em' : '0.9em' }}>
                                In: {totals.totalInput} | Out: {totals.totalOutput} | ${totals.totalPrice.toFixed(4)}
                            </span>
                            {modelRecognized ? (
                                <Dropdown
                                    selection
                                    compact={isInSplitPanel}
                                    options={MODEL_OPTIONS.map(m => ({ key: m, text: m, value: m }))}
                                    value={selectedModel}
                                    onChange={(_, data) => handleModelChange(data.value as string)}
                                />
                            ) : (
                                <span style={{
                                    padding: '6px 10px',
                                    backgroundColor: '#fff3cd',
                                    border: '1px solid #ffc107',
                                    borderRadius: '4px',
                                    fontSize: '0.85em'
                                }}>
                                    Model: {originalModel}
                                </span>
                            )}
                            {canToggleThinking && (
                                <Button
                                    size="mini"
                                    color={thinkingActive ? 'grey' : undefined}
                                    onClick={toggleThinkingMode}
                                    title={thinkingActive ? "Switch to non-thinking variant" : "Switch to thinking variant"}
                                >
                                    <Icon name="lightbulb outline" />
                                    {thinkingActive ? 'Thinking ON' : 'Thinking OFF'}
                                </Button>
                            )}
                        </div>
                    </div>

                    {thinkingActive && (
                        <div style={{
                            marginBottom: '8px',
                            padding: '6px 10px',
                            backgroundColor: '#f3e5f5',
                            border: '1px solid #9c27b0',
                            borderRadius: '4px',
                            fontSize: '0.85em',
                            color: '#393737'
                        }}>
                            <Icon name="lightbulb outline" />
                            {canToggleThinking
                                ? 'Extended thinking is enabled. Responses will include a thinking block. You can fall back to the non-thinking variant using the toggle above.'
                                : 'This model always runs in thinking mode. Responses will include a thinking block.'}
                        </div>
                    )}

                    {conversation.length > 2 && (
                        <Button size="small" onClick={removeLastPair}>
                            <Icon name="undo" /> Remove Last Exchange
                        </Button>
                    )}
                    {showModelWarning && selectedModel !== originalModel && (
                        <div style={{
                            marginTop: '10px',
                            padding: '8px 12px',
                            backgroundColor: '#f4d673',
                            border: '1px solid #ffc107',
                            borderRadius: '4px',
                            fontSize: '0.85em'
                        }}>
                            <Icon name="warning sign" />
                            Model mismatch: {selectedModel} vs {originalModel}
                        </div>
                    )}
                </div>

                <div style={{ padding: isInSplitPanel ? '10px' : '20px' }}>
                    {conversation.map((msg, idx) => {
                        const expanded = !!expandedThinkingMap[idx];
                        return (
                            <div
                                key={idx}
                                style={{
                                    marginBottom: isInSplitPanel ? '10px' : '15px',
                                    padding: isInSplitPanel ? '10px' : '15px',
                                    borderRadius: '8px',
                                    backgroundColor: msg.role === 'user' ? '#e3f2fd' : '#f1f8e9',
                                    border: msg.role === 'user' ? '2px solid #2196f3' : '2px solid #8bc34a',
                                }}
                            >
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: '8px',
                                    flexWrap: 'wrap',
                                    gap: '5px'
                                }}>
                                    <strong style={{
                                        color: msg.role === 'user' ? '#1976d2' : '#558b2f',
                                        fontSize: isInSplitPanel ? '0.85em' : '1em'
                                    }}>
                                        {msg.role.toUpperCase()}
                                        {msg.thinkingMessage && (
                                            <Label size="mini" color="grey" style={{ marginLeft: '6px' }}>
                                                <Icon name="lightbulb outline" /> thinking
                                            </Label>
                                        )}
                                    </strong>
                                    <div style={{
                                        display: 'flex',
                                        gap: '8px',
                                        alignItems: 'center',
                                        fontSize: isInSplitPanel ? '0.75em' : '0.85em'
                                    }}>
                                        {msg.promptTokens != null && <span>In: {msg.promptTokens}</span>}
                                        {msg.completionTokens != null && <span>Out: {msg.completionTokens}</span>}
                                        {msg.price != null && <span>${msg.price.toFixed(4)}</span>}
                                        <span style={{ color: '#666' }}>{new Date(msg.timestamp).toLocaleTimeString()}</span>
                                        <Icon name="copy" link onClick={() => copyToClipboard(msg.content)} />
                                    </div>
                                </div>

                                {msg.thinkingMessage && (
                                    <div style={{ marginBottom: '10px' }}>
                                        <Button
                                            size="mini"
                                            basic
                                            color="grey"
                                            onClick={() => toggleExpandedThinking(idx)}
                                        >
                                            <Icon name={expanded ? 'chevron down' : 'chevron right'} />
                                            {expanded ? 'Hide' : 'Show'} thinking ({msg.thinkingMessage.length} chars)
                                        </Button>
                                        {expanded && (
                                            <div style={{
                                                marginTop: '8px',
                                                padding: '10px 12px',
                                                backgroundColor: '#f3e5f5',
                                                border: '1px dashed #9c27b0',
                                                borderRadius: '6px',
                                                whiteSpace: 'pre-wrap',
                                                fontFamily: 'monospace',
                                                fontSize: isInSplitPanel ? '0.85em' : '0.9em',
                                                color: '#333333',
                                                maxHeight: '300px',
                                                overflow: 'auto'
                                            }}>
                                                <div style={{
                                                    fontSize: '0.75em',
                                                    color: '#636161',
                                                    marginBottom: '4px',
                                                    fontFamily: 'sans-serif',
                                                    fontWeight: 'bold'
                                                }}>
                                                    💭 THINKING BLOCK
                                                </div>
                                                {msg.thinkingMessage}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div style={{
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: 'monospace',
                                    fontSize: isInSplitPanel ? '0.9em' : '0.95em'
                                }}>
                                    {msg.content}
                                </div>
                            </div>
                        );
                    })}
                    <div ref={chatEndRef} />
                </div>

                <div style={{
                    position: 'sticky',
                    bottom: 0,
                    padding: isInSplitPanel ? '10px' : '15px',
                    backgroundColor: '#fff',
                    borderTop: '2px solid #ddd'
                }}>
                    <Input
                        fluid
                        placeholder="Type your message..."
                        value={userInput}
                        onChange={(e) => setUserInput(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage();
                            }
                        }}
                        action={
                            <Button
                                primary
                                onClick={sendMessage}
                                loading={conversationLoading}
                                disabled={!userInput.trim()}
                            >
                                Send
                            </Button>
                        }
                    />
                </div>
            </div>
        );
    }

    function renderTable() {
        const hasFilters = !!(searchId || searchInput || searchOutput || typeFilter || filterThinkingOnly);

        return (
            <>
                <Segment>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {!transportOrderMode ? (
                            <>
                                <Button.Group>
                                    <Button
                                        active={inputMode === 'id'}
                                        onClick={() => setInputMode('id')}
                                    >By ID</Button>
                                    <Button
                                        active={inputMode === 'json'}
                                        onClick={() => {
                                            setInputMode('json');
                                            setJsonInputModalOpen(true);
                                        }}
                                    >Paste JSON</Button>
                                </Button.Group>

                                {inputMode === 'id' && (
                                    <>
                                        <Input
                                            placeholder="Transport Order ID..."
                                            value={transportOrderId}
                                            onChange={(e) => setTransportOrderId(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && transportOrderId.trim() && loadTransportOrder()}
                                            style={{ width: '200px' }}
                                        />
                                        <Button
                                            primary
                                            onClick={loadTransportOrder}
                                            loading={loading && !!transportOrderId.trim()}
                                            disabled={!transportOrderId.trim()}
                                        >Load Order</Button>
                                    </>
                                )}
                                <div style={{ borderLeft: '1px solid #ccc', height: '30px', margin: '0 10px' }} />
                            </>
                        ) : (
                            <>
                                <Label size="large" color="blue">
                                    <Icon name="file" />
                                    {loadedTransportOrder?._id}
                                    <Label.Detail>{displayedResults.length} AI requests</Label.Detail>
                                </Label>
                                {base64Warning && (
                                    <Label color="orange" size="small">
                                        <Icon name="warning sign" />
                                        {base64Warning.removed} attachment(s) removed
                                    </Label>
                                )}
                                <Button onClick={exitTransportOrderMode}>
                                    <Icon name="close" /> Exit
                                </Button>
                                <Button
                                    icon={splitView ? 'compress' : 'expand'}
                                    content={splitView ? 'Full Table' : 'Split View'}
                                    onClick={() => setSplitView(!splitView)}
                                />
                                <div style={{ borderLeft: '1px solid #ccc', height: '30px', margin: '0 10px' }} />
                            </>
                        )}

                        <Input
                            placeholder="ID..."
                            value={searchId}
                            onChange={(e) => setSearchId(e.target.value)}
                            style={{ width: '150px' }}
                            disabled={transportOrderMode}
                        />
                        <Input
                            placeholder="Input contains..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            style={{ width: '180px' }}
                            disabled={transportOrderMode}
                        />
                        <Input
                            placeholder="Output contains..."
                            value={searchOutput}
                            onChange={(e) => setSearchOutput(e.target.value)}
                            style={{ width: '180px' }}
                            disabled={transportOrderMode}
                        />
                        <Input
                            placeholder="Type..."
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            style={{ width: '120px' }}
                            disabled={transportOrderMode}
                        />
                        <Dropdown
                            selection
                            options={LIMIT_OPTIONS}
                            value={limit}
                            onChange={(_, data) => setLimit(data.value as number)}
                            style={{ minWidth: '100px' }}
                            disabled={transportOrderMode}
                        />

                        <Checkbox
                            toggle
                            label="Thinking only"
                            checked={filterThinkingOnly}
                            onChange={(_, data) => setFilterThinkingOnly(data.checked || false)}
                        />

                        {!transportOrderMode && (
                            <>
                                <Button primary onClick={handleSearch} loading={loading}>Search</Button>
                                {hasFilters && (
                                    <Button onClick={clearFilters} disabled={loading}>Clear</Button>
                                )}
                            </>
                        )}
                        <div style={{ marginLeft: 'auto' }}>
                            <Checkbox
                                toggle
                                label="Auto-refresh (15s)"
                                checked={autoRefresh}
                                onChange={(_, data) => setAutoRefresh(data.checked || false)}
                                disabled={transportOrderMode}
                            />
                        </div>
                    </div>
                </Segment>

                <Table celled striped compact>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell width={2}>ID</Table.HeaderCell>
                            <Table.HeaderCell width={1}>Type</Table.HeaderCell>
                            <Table.HeaderCell width={2}>Model</Table.HeaderCell>
                            <Table.HeaderCell width={4}>Input</Table.HeaderCell>
                            <Table.HeaderCell width={4}>Output</Table.HeaderCell>
                            <Table.HeaderCell width={1}>Tokens (I/O)</Table.HeaderCell>
                            <Table.HeaderCell width={1}>Price</Table.HeaderCell>
                            <Table.HeaderCell width={1}>Time</Table.HeaderCell>
                            <Table.HeaderCell width={3}>Actions</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {displayedResults.map((result) => (
                            <Table.Row key={result._id}>
                                <Table.Cell style={{ fontSize: '0.95em', fontFamily: 'monospace' }}>
                                    {result._id}
                                </Table.Cell>
                                <Table.Cell>{result.type}</Table.Cell>
                                <Table.Cell>
                                    {result.model}
                                    {result.thinkingMessage && (
                                        <div>
                                            <Label size="mini" color="grey">
                                                <Icon name="lightbulb outline" /> thinking
                                            </Label>
                                        </div>
                                    )}
                                </Table.Cell>
                                <Table.Cell style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '250px' }}>
                                    {result.input}
                                </Table.Cell>
                                <Table.Cell style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '250px' }}>
                                    {result.output}
                                </Table.Cell>
                                <Table.Cell>
                                    {result.promptTokens || '-'} / {result.completionTokens || '-'}
                                    {result.counter > 1 &&
                                        <div style={{ fontSize: '0.9em', color: '#666' }}>
                                            ({result.counter}x reused by cache)
                                        </div>
                                    }
                                </Table.Cell>
                                <Table.Cell>${result.totalPrice?.toFixed(4)}</Table.Cell>
                                <Table.Cell>{new Date(result.timestamp).toLocaleString()}</Table.Cell>
                                <Table.Cell>
                                    <Button.Group size="mini">
                                        <Button onClick={() => openPreview(result)}>Preview</Button>
                                        <Button color="blue" onClick={() => openChat(result)}>Chat</Button>
                                        <Button color="teal" onClick={() => openCompare(result)}>Compare</Button>
                                        <Button color="olive" onClick={() => openRerun(result)}>Rerun</Button>
                                        <Button color="orange" onClick={() => openEditPrompt(result)}>Edit</Button>
                                    </Button.Group>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </>
        );
    }

    function renderCompareModal() {
        if (!compareOpen || !compareSource) return null;

        const sourceModelEnum = mapResultToEnum(compareSource);
        const sourceOutputValue = sourceIsPrettified && sourcePrettifiedOutput
            ? sourcePrettifiedOutput
            : (compareSource.rawOutput || compareSource.output);
        const sourceDisplayValue = sourceShowInput ? compareSource.input : sourceOutputValue;
        const totalColumns = compareSlots.length + 1;
        const editorFont = 14;
        const inputCharCount = compareSource.input.length.toLocaleString();

        function disabledOptionsForSlot(slotId: string): Set<string> {
            return new Set<string>([
                sourceModelEnum,
                ...compareSlots.filter(s => s.id !== slotId).map(s => s.model)
            ]);
        }

        return (
            <Modal
                open={compareOpen}
                onClose={closeCompare}
                size="fullscreen"
            >
                <Header style={{
                    padding: '12px 20px',
                    backgroundColor: '#2c3e50',
                    color: '#fff',
                    margin: 0
                }}>
                    <Icon name="exchange" /> Compare Models
                    <Label color="blue" size="small" style={{ marginLeft: 10 }}>
                        Source: {compareSource.model}
                    </Label>
                    {compareSource.thinkingMessage && (
                        <Label color="grey" size="small" style={{ marginLeft: 6 }}>
                            <Icon name="lightbulb outline" /> Thinking
                        </Label>
                    )}
                </Header>

                <Modal.Content
                    style={{
                        padding: '12px 14px',
                        height: 'calc(100vh - 160px)',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {/* Toolbar */}
                    <Segment style={{ flexShrink: 0, padding: '10px 12px', marginBottom: 10 }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                            <strong style={{ fontSize: '1em' }}>
                                Compare against ({compareSlots.length}/{MAX_COMPARE_SLOTS}):
                            </strong>

                            {compareSlots.map((slot) => {
                                const disabledSet = disabledOptionsForSlot(slot.id);
                                return (
                                    <div key={slot.id} style={{
                                        display: 'flex', alignItems: 'center', gap: 6,
                                        padding: '4px 6px', backgroundColor: '#f8f9fa',
                                        border: '1px solid #ddd', borderRadius: 4
                                    }}>
                                        <Dropdown
                                            selection
                                            search
                                            options={MODEL_OPTIONS.map(m => ({
                                                key: m, text: m, value: m,
                                                disabled: disabledSet.has(m)
                                            }))}
                                            value={slot.model}
                                            disabled={compareRunning}
                                            onChange={(_, data) => changeSlotModel(slot.id, data.value as string)}
                                            style={{ minWidth: 280, fontSize: '0.95em' }}
                                        />
                                        <Button size="mini" icon="close" basic
                                                disabled={compareRunning}
                                                onClick={() => removeCompareSlot(slot.id)}
                                                title="Remove" />
                                    </div>
                                );
                            })}

                            {compareSlots.length < MAX_COMPARE_SLOTS && (
                                <Button size="small" onClick={addCompareSlot} disabled={compareRunning}>
                                    <Icon name="plus" /> Add Model
                                </Button>
                            )}

                            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                {compareSlots.some(s => s.status === 'done') && (
                                    <Button onClick={clearAllCompareResults} disabled={compareRunning}>
                                        <Icon name="eraser" /> Clear Results
                                    </Button>
                                )}
                                <Button
                                    primary
                                    loading={compareRunning}
                                    onClick={runComparison}
                                    disabled={pendingSlotsCount === 0 || compareRunning}
                                >
                                    <Icon name="play" />
                                    {pendingSlotsCount > 0 ? `Run ${pendingSlotsCount} pending` : 'Nothing to run'}
                                </Button>
                            </div>
                        </div>

                        <div style={{ marginTop: 8, fontSize: '0.9em', color: '#555', lineHeight: 1.5 }}>
                            Same input (<strong>{inputCharCount} chars</strong>) sent in parallel to each selected model.
                            The cached <strong>{compareSource.model}</strong> result on the left is never re-sent.
                            Changing one model only resets that slot — other completed results are kept.
                        </div>
                    </Segment>

                    {/* Columns */}
                    <div style={{
                        flex: 1,
                        minHeight: 0,
                        display: 'grid',
                        gridTemplateColumns: `repeat(${totalColumns}, minmax(0, 1fr))`,
                        gap: 10
                    }}>
                        {/* ORIGINAL */}
                        <div style={{
                            display: 'flex', flexDirection: 'column', minHeight: 0,
                            border: '2px solid #2185d0', borderRadius: 6, padding: 10,
                            backgroundColor: '#f7fbff'
                        }}>
                            <div style={{
                                display: 'flex', justifyContent: 'space-between',
                                alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <strong style={{ fontSize: '0.95em' }}>
                                        {compareSource.model}{' '}
                                        <Label size="mini" color="blue">ORIGINAL</Label>
                                    </strong>
                                    <StatsRow
                                        inTokens={compareSource.promptTokens}
                                        outTokens={compareSource.completionTokens}
                                        price={compareSource.totalPrice}
                                    />
                                </div>
                                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                    <Button.Group size="mini">
                                        <Button
                                            active={!sourceShowInput}
                                            onClick={() => setSourceShowInput(false)}
                                        >Output</Button>
                                        <Button
                                            active={sourceShowInput}
                                            onClick={() => setSourceShowInput(true)}
                                        >Input</Button>
                                    </Button.Group>
                                    {!sourceShowInput && (sourceIsPrettified ? (
                                        <Button size="mini" basic onClick={unprettifySource} title="Show original">
                                            <Icon name="undo" /> Unprettify
                                        </Button>
                                    ) : (
                                        <Button size="mini" basic onClick={prettifySource}
                                                loading={sourcePrettifyLoading} disabled={sourcePrettifyLoading}
                                                title="Prettify JSON">
                                            <Icon name="code" /> Prettify
                                        </Button>
                                    ))}
                                    {sourcePrettifyError && (
                                        <Label size="mini" color="red">{sourcePrettifyError}</Label>
                                    )}
                                    <Button size="mini" basic icon="copy"
                                            onClick={() => copyToClipboard(sourceDisplayValue)} />
                                    <Button
                                        size="mini"
                                        color="blue"
                                        onClick={() => { setCompareOpen(false); openChat(compareSource); }}
                                        title={`Open chat using ${compareSource.model} (source)`}
                                    >
                                        <Icon name="comments" /> Chat
                                    </Button>
                                </div>
                            </div>

                            <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                                <AceEditor
                                    key={`compare-source-${compareSource._id}-${sourceShowInput ? 'in' : 'out'}-${sourceIsPrettified ? 'p' : 'r'}`}
                                    mode="json"
                                    theme="monokai"
                                    value={sourceDisplayValue}
                                    readOnly
                                    width="100%"
                                    height="100%"
                                    showPrintMargin={false}
                                    fontSize={editorFont}
                                    wrapEnabled
                                />
                            </div>

                            {compareSource.thinkingMessage && (
                                <ThinkingPanel
                                    content={compareSource.thinkingMessage}
                                    expanded={compareShowSourceThinking}
                                    onToggle={() => setCompareShowSourceThinking(v => !v)}
                                />
                            )}
                        </div>

                        {/* COMPARISON COLUMNS */}
                        {compareSlots.map(slot => {
                            const isLoading = slot.status === 'loading';
                            const isError = slot.status === 'error';
                            const isDone = slot.status === 'done';
                            const value = slot.isPrettified && slot.prettifiedOutput
                                ? slot.prettifiedOutput
                                : (slot.output || (isError ? `❌ ${slot.error || ''}` : ''));

                            return (
                                <div key={slot.id} style={{
                                    display: 'flex', flexDirection: 'column', minHeight: 0,
                                    border: isError ? '2px solid #db2828'
                                        : isDone ? '2px solid #21ba45'
                                            : '1px solid #ccc',
                                    borderRadius: 6, padding: 10, backgroundColor: '#fff'
                                }}>
                                    <div style={{
                                        display: 'flex', justifyContent: 'space-between',
                                        alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                            <strong style={{ fontSize: '0.95em' }}>
                                                {slot.model}
                                                {isLoading && <Icon name="spinner" loading style={{ marginLeft: 6 }} />}
                                                {isDone && <Icon name="check circle" color="green" style={{ marginLeft: 6 }} />}
                                                {isError && <Icon name="times circle" color="red" style={{ marginLeft: 6 }} />}
                                            </strong>
                                            <StatsRow
                                                inTokens={slot.promptTokens}
                                                outTokens={slot.completionTokens}
                                                price={slot.price}
                                                durationMs={slot.durationMs}
                                                status={slot.status}
                                            />
                                        </div>
                                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                            {isDone && slot.output && (
                                                <>
                                                    {slot.isPrettified ? (
                                                        <Button size="mini" basic onClick={() => unprettifySlot(slot.id)}>
                                                            <Icon name="undo" /> Unprettify
                                                        </Button>
                                                    ) : (
                                                        <Button size="mini" basic onClick={() => prettifySlot(slot.id)}
                                                                loading={!!slot.prettifyLoading}
                                                                disabled={!!slot.prettifyLoading}>
                                                            <Icon name="code" /> Prettify
                                                        </Button>
                                                    )}
                                                    {slot.prettifyError && (
                                                        <Label size="mini" color="red">{slot.prettifyError}</Label>
                                                    )}
                                                    <Button size="mini" basic icon="copy"
                                                            onClick={() => copyToClipboard(slot.output!)} />
                                                    <Button
                                                        size="mini"
                                                        color="blue"
                                                        onClick={() => openChatFromSlot(slot)}
                                                        title={`Open chat using ${slot.model}'s output`}
                                                    >
                                                        <Icon name="comments" /> Chat
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
                                        <AceEditor
                                            key={`compare-${slot.id}-${slot.isPrettified ? 'p' : 'r'}`}
                                            mode="json"
                                            theme="monokai"
                                            value={value}
                                            readOnly
                                            width="100%"
                                            height="100%"
                                            showPrintMargin={false}
                                            fontSize={editorFont}
                                            wrapEnabled
                                        />
                                    </div>

                                    {slot.thinkingMessage && (
                                        <ThinkingPanel
                                            content={slot.thinkingMessage}
                                            expanded={!!slot.showThinking}
                                            onToggle={() => updateCompareSlot(slot.id, { showThinking: !slot.showThinking })}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </Modal.Content>

                <Modal.Actions>
                    <Button onClick={closeCompare}>Close</Button>
                </Modal.Actions>
            </Modal>
        );
    }

    function StatsRow(props: {
        inTokens?: number;
        outTokens?: number;
        price?: number;
        durationMs?: number;
        status?: 'idle' | 'loading' | 'done' | 'error';
    }) {
        const { inTokens, outTokens, price, durationMs, status } = props;

        if (status === 'loading') {
            return <span style={{ fontSize: '0.82em', color: '#888' }}>Running…</span>;
        }
        if (status === 'idle') {
            return <span style={{ fontSize: '0.82em', color: '#888' }}>Idle</span>;
        }

        const items: { label: string; value: string }[] = [];
        if (inTokens != null)   items.push({ label: 'IN',  value: inTokens.toLocaleString() });
        if (outTokens != null)  items.push({ label: 'OUT', value: outTokens.toLocaleString() });
        if (price != null)      items.push({ label: '$',   value: price.toFixed(4) });
        if (durationMs != null) items.push({ label: 'T',   value: `${(durationMs / 1000).toFixed(2)}s` });
        if (items.length === 0) return null;

        return (
            <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4, fontSize: '0.78em' }}>
            {items.map(it => (
                <span key={it.label} style={{
                    padding: '2px 7px',
                    backgroundColor: '#eef0f2',
                    border: '1px solid #dde1e4',
                    borderRadius: 10,
                    fontWeight: 500
                }}>
                    <span style={{ color: '#888', marginRight: 3 }}>{it.label}</span>
                    <span style={{ color: '#222' }}>{it.value}</span>
                </span>
            ))}
        </span>
        );
    }

    function ThinkingPanel(props: { content: string; expanded: boolean; onToggle: () => void; }) {
        return (
            <div style={{ marginTop: '8px' }}>
                <Button size="mini" basic color="grey" onClick={props.onToggle}>
                    <Icon name={props.expanded ? 'chevron down' : 'chevron right'} />
                    {props.expanded ? 'Hide' : 'Show'} thinking ({props.content.length.toLocaleString()} chars)
                </Button>
                {props.expanded && (
                    <div style={{
                        marginTop: '6px',
                        maxHeight: '320px',
                        overflow: 'auto',
                        backgroundColor: '#f3e5f5',
                        border: '1px dashed #9c27b0',
                        padding: '10px 12px',
                        fontSize: '0.9em',
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        color: '#222'
                    }}>
                        {props.content}
                    </div>
                )}
            </div>
        );
    }

    function renderRightPanelContent() {
        if (previewOpen && selectedResult) return renderInlinePreview();
        if (chatOpen) return renderChatView(true);
        return (
            <div style={{ overflow: 'auto', height: '100%', padding: '10px' }}>
                {renderTable()}
            </div>
        );
    }

    function openChatFromSlot(slot: CompareSlot) {
        if (!compareSource || !slot.output || slot.status !== 'done') return;
        const pseudoResult: AiParseResult = {
            ...compareSource,
            _id: `${compareSource._id}-cmp-${slot.id}`,
            model: slot.model,
            input: compareSource.input,
            output: slot.output,
            rawOutput: slot.output,
            promptTokens: slot.promptTokens,
            completionTokens: slot.completionTokens,
            totalPrice: slot.price ?? 0,
            thinkingMessage: slot.thinkingMessage,
            timestamp: new Date().toISOString(),
        } as AiParseResult;
        setCompareOpen(false);
        openChat(pseudoResult);
    }

    function renderMainContent() {
        if (splitView && transportOrderMode) {
            return (
                <div
                    id="split-container"
                    style={{
                        display: 'flex',
                        height: '100vh',
                        overflow: 'hidden',
                        position: 'relative'
                    }}
                >
                    <div style={{
                        width: `${splitPosition}%`,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0
                    }}>
                        <TransportOrderJsonViewer
                            transportOrder={transportOrderJson}
                            readOnly={true}
                            onChange={() => {}}
                        />
                    </div>

                    <div
                        onMouseDown={handleMouseDown}
                        style={{
                            width: '5px',
                            cursor: 'col-resize',
                            backgroundColor: isResizing ? '#2185d0' : '#ddd',
                            flexShrink: 0,
                            transition: isResizing ? 'none' : 'background-color 0.2s',
                            userSelect: 'none'
                        }}
                        onMouseEnter={(e) => {
                            if (!isResizing) e.currentTarget.style.backgroundColor = '#2185d0';
                        }}
                        onMouseLeave={(e) => {
                            if (!isResizing) e.currentTarget.style.backgroundColor = '#ddd';
                        }}
                    />

                    <div style={{
                        width: `${100 - splitPosition}%`,
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        overflow: 'hidden'
                    }}>
                        {renderRightPanelContent()}
                    </div>
                </div>
            );
        }

        if (chatOpen) {
            return (
                <div style={{ height: '100vh', overflow: 'hidden' }}>
                    {renderChatView(false)}
                </div>
            );
        }

        return (
            <div style={{ padding: '20px' }}>
                {renderTable()}
            </div>
        );
    }

    return (
        <>
            {renderMainContent()}
            {renderPreviewModal()}
            {renderJsonInputModal()}
            {renderCompareModal()}
            <AiRequestRerunModal
                open={rerunOpen}
                onClose={() => setRerunOpen(false)}
                source={rerunSource}
                model={rerunSource ? mapResultToEnum(rerunSource) : ""}
                modelRecognized={rerunSource ? isModelRecognized(mapResultToEnum(rerunSource)) : false}
            />
            <AiRequestEditPromptModal
                open={editPromptOpen}
                onClose={() => setEditPromptOpen(false)}
                source={editPromptSource}
                model={editPromptSource ? mapResultToEnum(editPromptSource) : ""}
                modelRecognized={editPromptSource ? isModelRecognized(mapResultToEnum(editPromptSource)) : false}
                modelOptions={MODEL_OPTIONS}
            />
        </>
    );
}